import { SnapshotCard } from '@ovComponents/3-pattern/snapshotCard';
import { Box, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type { FlagGlossaryKpiCounts } from '../utils/flagGlossary.types';

const KPI_TITLE_SX = {
  fontWeight: 700,
  textTransform: 'uppercase',
} as const;

export type FlagGlossaryKpiSummaryProps = {
  kpiCounts: FlagGlossaryKpiCounts;
  loading: boolean;
};

const KPI_LOADING_PLACEHOLDER = '—';

export const FlagGlossaryKpiSummary = ({
  kpiCounts,
  loading,
}: FlagGlossaryKpiSummaryProps) => {
  const { t } = useTranslation(['reconciliation']);

  const kpiTitle = (titleKey: string) => (
    <Typography
      component='span'
      variant='labelSmall'
      color='textSecondary'
      sx={KPI_TITLE_SX}
    >
      {t(titleKey)}
    </Typography>
  );

  const kpiValue = (value: number) =>
    loading ? KPI_LOADING_PLACEHOLDER : value.toLocaleString();

  const showTurnedOff = loading || kpiCounts.turnedOff > 0;

  return (
    <Box
      sx={{
        display: 'grid',

        gridTemplateColumns: {
          xs: '1fr',
          sm: showTurnedOff ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
        },

        gap: 1.5,
      }}
    >
      <SnapshotCard
        title={kpiTitle('flagGlossary.kpi.total')}
        primaryValue={kpiValue(kpiCounts.total)}
        tone='neutral'
        surface='accentBorder'
        footer={t('flagGlossary.kpi.totalHint')}
        compact
      />
      <SnapshotCard
        title={kpiTitle('flagGlossary.kpi.thresholded')}
        primaryValue={kpiValue(kpiCounts.thresholded)}
        tone='primary'
        surface='accentBorder'
        footer={t('flagGlossary.kpi.thresholdedHint')}
        compact
      />
      {showTurnedOff && (
        <SnapshotCard
          title={kpiTitle('flagGlossary.kpi.turnedOff')}
          primaryValue={kpiValue(kpiCounts.turnedOff)}
          tone='warning'
          surface='accentBorder'
          footer={t('flagGlossary.kpi.turnedOffHint')}
          compact
        />
      )}
    </Box>
  );
};
